import { getMyCompany, companyMembersByCompany } from '../graphql/queries.js';
import { createCompanyMember, updateCompanyMember, deleteCompanyMember } from '../graphql/mutations.js';
import { refreshOnce } from '../auth.js';

const APPSYNC_URL = process.env.REACT_APP_APPSYNC_API_URL;

export function isExpired(token, skew = 60) {
  try {
    const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !p.exp || (Date.now() / 1000) >= (p.exp - skew);
  } catch { return true; }
}

async function graphqlRequest({ query, variables }, { retried = false } = {}) {
  let idToken = localStorage.getItem('id_token');

  if (idToken && isExpired(idToken)) {
    await refreshOnce();
    idToken = localStorage.getItem('id_token');
  }

  if (!idToken) {
    console.warn('[CompanyService graphqlRequest] No id_token in localStorage');
    const error = new Error('User is not authenticated');
    error.name = 'NoValidAuthTokens';
    throw error;
  }

  const body = { query, variables: variables || {} };

  const res = await fetch(APPSYNC_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify(body),
  });
  if (res.status === 401 && !retried) {
    await refreshOnce();
    return graphqlRequest({ query, variables }, { retried: true });
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    console.error('[CompanyService graphqlRequest] HTTP error', res.status, text);
    throw new Error(`GraphQL HTTP error ${res.status}`);
  }

  const data = await res.json();

  if (data.errors) {
    console.error('[CompanyService graphqlRequest] GraphQL errors (full):', JSON.stringify(data.errors, null, 2));
    const messages = data.errors.map((e) => e.message).join('\n');
    const err = new Error(messages);
    err.errors = data.errors;
    throw err;
  }


  return data;
}

class CompanyService {
  async getMyCompany() {
    try {
      const result = await graphqlRequest({ query: getMyCompany, variables: {} });
      return result?.data?.getMyCompany || null;
    } catch (error) {
      if (error?.name === 'NoValidAuthTokens') throw error;

      if (error?.errors) {
        const msg = error.errors.map((e) => e.message).join('\n');
        if (msg.toLowerCase().includes('no company') || msg.toLowerCase().includes('membership')) {
          return null;
        }
        throw new Error(msg);
      }

      throw new Error(error?.message || 'Failed to load company');
    }
  }

  async listCompanyMembers(companyId, limit = 200) {
    const result = await graphqlRequest({
      query: companyMembersByCompany,
      variables: { companyId, limit },
    });

    return result?.data?.companyMembersByCompany?.items || [];
  }

  async createCompanyMember(input) {
    const result = await graphqlRequest({
      query: createCompanyMember,
      variables: { input },
    });
    return result?.data?.createCompanyMember || null;
  }

  async updateCompanyMember(input) {
    const result = await graphqlRequest({
      query: updateCompanyMember,
      variables: { input },
    });
    return result?.data?.updateCompanyMember || null;
  }

  async deleteCompanyMember(id) {
    const result = await graphqlRequest({
      query: deleteCompanyMember,
      variables: { input: { id } }, // matches typical Amplify input shape
    });
    return result?.data?.deleteCompanyMember || null;
  }
}

export default new CompanyService();
